import { Alert, Tabs, TabsContent, TabsList, TabsTrigger } from "@station/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import {
  AllocationKeysTab,
  CategoriesTab,
  CompanyCardsTab,
  MetersTab,
  RecurringTab,
} from "../../../features/expenses/definitions.js";
import { ExpensesPage, useExpensePermissions, useExpensesCrumb } from "../../../features/expenses/shared.js";

const TABS = ["categories", "allocation-keys", "meters", "cards", "recurring"] as const;
const searchSchema = z.object({
  ...companySearchShape,
  tab: z.enum(TABS).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/giderler/tanimlar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: DefinitionsPage,
});

function DefinitionsPage(): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const crumb = useExpensesCrumb("/giderler");
  const permissions = useExpensePermissions();
  const tab = search.tab ?? "categories";
  // Yetkisi olmayan kullanıcı tanımları SALT OKUNUR görür (ekran gizlenmez).
  const readOnly = !permissions.canManageDefinitions;

  return (
    <ExpensesPage
      title={t("menu.expenseDefinitions")}
      description={t("expenses.definitions.description")}
      breadcrumb={[crumb, { label: t("menu.expenseDefinitions") }]}
    >
      {({ companyId }) => (
        <div className="grid gap-4">
          {readOnly ? <Alert tone="info">{t("expenses.definitions.readOnly")}</Alert> : null}
          <Tabs
            value={tab}
            onValueChange={(next) =>
              void navigate({
                search: (previous) => ({ ...previous, tab: next as (typeof TABS)[number] }),
                replace: true,
              })
            }
          >
            <TabsList aria-label={t("expenses.definitions.tabs")}>
              <TabsTrigger value="categories">{t("expenses.definitions.categories")}</TabsTrigger>
              <TabsTrigger value="allocation-keys">{t("expenses.definitions.allocationKeys")}</TabsTrigger>
              <TabsTrigger value="meters">{t("expenses.definitions.meters")}</TabsTrigger>
              <TabsTrigger value="cards">{t("expenses.definitions.companyCards")}</TabsTrigger>
              <TabsTrigger value="recurring">{t("expenses.definitions.recurring")}</TabsTrigger>
            </TabsList>
            <TabsContent value="categories">
              {tab === "categories" ? <CategoriesTab companyId={companyId} readOnly={readOnly} /> : null}
            </TabsContent>
            <TabsContent value="allocation-keys">
              {tab === "allocation-keys" ? (
                <AllocationKeysTab companyId={companyId} readOnly={readOnly} />
              ) : null}
            </TabsContent>
            <TabsContent value="meters">
              {tab === "meters" ? <MetersTab companyId={companyId} readOnly={readOnly} /> : null}
            </TabsContent>
            <TabsContent value="cards">
              {tab === "cards" ? <CompanyCardsTab companyId={companyId} readOnly={readOnly} /> : null}
            </TabsContent>
            <TabsContent value="recurring">
              {tab === "recurring" ? <RecurringTab companyId={companyId} readOnly={readOnly} /> : null}
            </TabsContent>
          </Tabs>
        </div>
      )}
    </ExpensesPage>
  );
}
